/**
 * Network-level fetch failures as a queryable metric, not an app exception.
 *
 * `log.error` reaches console.error in production, and PostHog's
 * `capture_exceptions` autocaptures console errors: so every transient fetch
 * rejection used to mint a `$exception`. Incident 2026-07-09: a user's three
 * dashboard tabs each threw `TypeError: Failed to fetch` on `/auth/bootstrap`
 * at the same millisecond after 34 minutes idle (a laptop wake, where Chrome
 * fires `online` before the network can carry a request). TanStack's `retry: 1`
 * healed it a second later: exactly three events, not six, because a
 * successful retry logs nothing. The user saw nothing. Error Tracking did.
 *
 * So: `client.ts` records every rejection as `api_network_failure` (countable,
 * broken down by endpoint: a real outage still shows up loudly), and `main.tsx`
 * raises the exception only for a failure that survived every retry and actually
 * reached the user.
 *
 * No logger, SDK or environment reads: the payload includes a snapshot of
 * browser connectivity at the moment of failure, before telemetry is queued.
 */
import { NetworkError } from './networkError';
import { TimeoutError } from './requestTimeout';
import { readBrowserNetworkState } from './networkDiagnostics';

export const NETWORK_FAILURE_EVENT = 'api_network_failure';

export interface NetworkFailureProps extends Record<string, unknown> {
  /** Path only. Query strings carry review tokens (`?s=`) and must never ship. */
  endpoint: string;
  reason: string;
  /**
   * True when the request hit its own deadline rather than being refused.
   *
   * Neither a rejection nor a timeout proves whether the server received the
   * request: a response can be lost, or the browser can stop waiting before
   * anything reaches the API. `timeout_ms` identifies the client deadline;
   * correlate server logs before attributing a cause. See GitHub issue #75.
   */
  timed_out: boolean;
  timeout_ms?: number;
}

/** Human-readable reason for an unknown thrown value. Never throws.
 * Shared with lib/sseStreamFailure.ts so the two metrics describe errors
 * identically. */
export function describeReason(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  if (typeof err === 'string' && err) return err;
  return 'unknown';
}

/** Build the analytics payload for a fetch rejection. Never throws. */
export function buildNetworkFailureProps(endpoint: string, err: unknown): NetworkFailureProps {
  const queryStart = endpoint.indexOf('?');
  const timedOut = err instanceof TimeoutError;
  return {
    ...readBrowserNetworkState(),
    endpoint: queryStart === -1 ? endpoint : endpoint.slice(0, queryStart),
    reason: describeReason(err),
    timed_out: timedOut,
    ...(timedOut && { timeout_ms: err.timeoutMs }),
  };
}

/**
 * True for a network-level fetch rejection. Used by the QueryCache
 * error handler in main.tsx, which runs only after `retry` is exhausted.
 */
export function isNetworkError(err: unknown): err is NetworkError {
  return err instanceof NetworkError;
}
